import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { uploadImageToCloudinary } from "../../lib/cloudinary";

const Row = ({ item, type, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-upload of same file
    if (!file) return;
    setBusy(true);
    setProgress(0);
    try {
      const uploaded = await uploadImageToCloudinary(file, {
        folder: `opikjewels/${type}s`,
        onProgress: (p) => setProgress(p),
      });
      const url = uploaded.secure_url || uploaded.url;
      if (!url) throw new Error("Upload returned no URL");
      await api.put("/admin/birthstones-zodiacs", {
        type,
        slug: item.slug,
        image_url: url,
      });
      toast.success(`Updated ${item.name}`);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const onReset = async () => {
    if (!item.is_overridden) return;
    if (!window.confirm(`Reset ${item.name} to default image?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/birthstones-zodiacs/${type}/${item.slug}`);
      toast.success(`${item.name} reset to default`);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`${type}-row-${item.slug}`}
      className="bg-onyx border border-white/10 p-5 flex flex-col"
    >
      <div className="aspect-[4/3] mb-4 overflow-hidden bg-black/40 relative">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/30 text-xs">
            no image
          </div>
        )}
        {item.is_overridden && (
          <span className="absolute top-2 right-2 font-accent text-[9px] tracking-[0.22em] uppercase bg-gold/90 text-black px-2 py-1">
            Custom
          </span>
        )}
        {busy && progress > 0 && progress < 100 && (
          <div className="absolute inset-x-0 bottom-0 bg-black/70 text-gold text-xs px-3 py-1 font-accent tracking-[0.2em] uppercase">
            Uploading {progress}%
          </div>
        )}
      </div>

      <div className="flex-1">
        <div className="font-heading text-xl text-ivory">{item.name}</div>
        <div className="font-accent text-[10px] tracking-[0.22em] uppercase text-white/40 mt-1">
          {type === "birthstone" ? `Month ${item.month}` : item.dates}
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <label
          data-testid={`${type}-upload-${item.slug}`}
          className={`flex-1 cursor-pointer text-center font-accent text-[10px] tracking-[0.24em] uppercase border px-3 py-2 ${
            busy
              ? "border-white/10 text-white/30 cursor-wait"
              : "border-gold text-gold hover:bg-gold hover:text-black"
          }`}
        >
          {busy ? "Working…" : "Change image"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            disabled={busy}
            onChange={onFile}
          />
        </label>
        {item.is_overridden && (
          <button
            data-testid={`${type}-reset-${item.slug}`}
            onClick={onReset}
            disabled={busy}
            className="font-accent text-[10px] tracking-[0.24em] uppercase border border-white/20 px-3 py-2 text-white/60 hover:text-white"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};

const BirthstoneZodiacEditor = () => {
  const [data, setData] = useState({ birthstones: [], zodiacs: [] });
  const [section, setSection] = useState("birthstones");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/birthstones-zodiacs")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const items = section === "birthstones" ? data.birthstones : data.zodiacs;
  const type = section === "birthstones" ? "birthstone" : "zodiac";

  return (
    <div data-testid="birthstone-zodiac-editor" className="space-y-8">
      <div>
        <h2 className="font-heading text-3xl text-ivory">
          Birthstones &amp; Zodiacs
        </h2>
        <p className="text-white/50 text-sm mt-2 max-w-2xl">
          Upload custom cover images for each birthstone month or zodiac sign.
          Changes appear immediately on the public site (
          <code className="text-gold">/birthstone/&lt;slug&gt;</code> and{" "}
          <code className="text-gold">/zodiac/&lt;slug&gt;</code>) and on the
          Find-Your-Stone page.
        </p>
      </div>

      <div className="flex gap-3 border-b border-white/10 pb-4">
        {[
          { k: "birthstones", l: "Birthstones (12)" },
          { k: "zodiacs", l: "Zodiacs (12)" },
        ].map((s) => (
          <button
            key={s.k}
            data-testid={`bz-tab-${s.k}`}
            onClick={() => setSection(s.k)}
            className={`font-accent text-[11px] tracking-[0.28em] uppercase px-5 py-3 ${
              section === s.k
                ? "text-gold border-b border-gold -mb-[17px] pb-4"
                : "text-white/50 hover:text-white"
            }`}
          >
            {s.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/50 text-sm">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {items.map((item) => (
            <Row key={item.slug} item={item} type={type} onUpdated={load} />
          ))}
        </div>
      )}
    </div>
  );
};

export default BirthstoneZodiacEditor;
